/**
 * Styles from https://mapstyle.withgoogle.com/
 * More styles can be found at https://snazzymaps.com/
 */

import { useColors, useIsDarkMode } from "@mallardbay/lib-react-components"

/**
 * Hook that generates map styles using theme colors only (no hex fallbacks)
 * Throws if any required color is missing.
 * @returns Array of map style objects for Google Maps
 */
export function useMapStyles() {
    const colors = useColors()
    const isDarkMode = useIsDarkMode()

    if (!isDarkMode) return []

    // Assert all required colors exist
    const bg = colors.gray[800]
    const road = colors.gray[700]
    const roadStroke = colors.gray[700]
    const highway = colors.gray[600]
    const boundary = colors.gray[500]
    const label = colors.text
    const park = colors.green[600]
    const water = colors.blue[600]

    const geometryStyle = { elementType: "geometry" as const }
    const textFillStyle = { elementType: "labels.text.fill" as const }
    const textStrokeStyle = { elementType: "labels.text.stroke" as const }
    const styler = (color: string) => ({ stylers: [{ color }] })

    return [
        { ...geometryStyle, ...styler(bg) },
        { ...textFillStyle, ...styler(label) },
        { ...textStrokeStyle, ...styler(bg) },
        // Boundaries
        {
            featureType: "administrative",
            elementType: "geometry",
            ...styler(boundary),
        },
        // Roads
        { featureType: "road", ...geometryStyle, ...styler(road) },
        {
            featureType: "road",
            elementType: "geometry.stroke",
            ...styler(roadStroke),
        },
        { featureType: "road", ...textFillStyle, ...styler(label) },
        // eslint-disable-next-line sonarjs/no-duplicate-string
        { featureType: "road.highway", ...geometryStyle, ...styler(highway) },
        {
            featureType: "road.highway",
            elementType: "geometry.stroke",
            ...styler(roadStroke),
        },
        { featureType: "road.highway", ...textFillStyle, ...styler(label) },
        // Parks/Vegetation
        { featureType: "poi.park", ...geometryStyle, ...styler(park) },
        { featureType: "poi.park", ...textFillStyle, ...styler(label) },
        // Water
        { featureType: "water", ...geometryStyle, ...styler(water) },
        { featureType: "water", ...textFillStyle, ...styler(label) },
        { featureType: "water", ...textStrokeStyle, ...styler(water) },
        // Transit
        { featureType: "transit", ...geometryStyle, ...styler(roadStroke) },
        { featureType: "transit.station", ...textFillStyle, ...styler(label) },
    ]
}
